import { Field } from 'formik'
import PropTypes from 'prop-types'
import React from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import { space } from 'styled-system'
import { Button, BUTTON_VARIANT } from 'components/Button'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Image } from 'components/Image'
import { Input } from 'components/Input'
import { Box, Flex, Grid } from 'components/Layout'
import { bordered } from 'components/mixins'
import { RadioGroup } from 'components/Radio'
import { Text } from 'components/Typography'
import { WrappedLoader } from 'components/WrappedLoader'
import { PAYMENT_OPTIONS } from 'constants/common'
import { useUserAuth } from 'providers/userAuth'
import {
  FONT_SIZE,
  SPACE,
  FONT_WEIGHT,
  RADIUS,
  BORDER_WIDTH,
  COLOR_INTENT,
} from 'Theme'
import { useMedia } from 'useMedia'
import { formatSavedCreditCardText } from 'utils/formatters'

const DEFAULT_CARD_FIELD_NAME = 'card'

const ElementsBorder = styled(Box)`
  ${bordered};
  border-radius: ${RADIUS.PX_10};
`

const CardInputWrapper = styled(Flex)`
  background-color: ${COLOR_INTENT.GRAY_3};
  border-radius: ${RADIUS.PX_6};
`

const PAYMENT_OPTIONS_SETUP = {
  [PAYMENT_OPTIONS.APPLE_PAY]: {
    label: 'Apple Pay',
    image: '/applePayMark.svg',
  },
  [PAYMENT_OPTIONS.CARD]: {
    label: 'Credit card',
  },
}

export const PaymentElements = ({
  setFieldValue,
  isDisabled,
  card: selectedCard,
  cardFieldName,
  paymentOption,
  isHidden,
  availablePaymentOptions,
  isLoadingOptions,
  postalCode,
  isCustomCheckout,
  ...props
}) => {
  const media = useMedia()

  const { user } = useUserAuth()

  return (
    <Flex
      flexDirection="column"
      mt={{ MOBILE: SPACE.PX_20, TABLET: SPACE.PX_30 }}
      mb={{ MOBILE: 0, TABLET: isCustomCheckout ? 0 : SPACE.PX_40 }}
      {...(isHidden ? { display: 'none' } : {})}
      {...props}
    >
      <Text
        fontWeight={FONT_WEIGHT.MEDIUM}
        fontSize={FONT_SIZE.PX_24}
        mb={{ MOBILE: SPACE.PX_15, TABLET: SPACE.PX_20 }}
      >
        Pay with
      </Text>
      {isLoadingOptions && (
        <WrappedLoader isFillSpace={false} p={SPACE.PX_30} />
      )}
      {!isLoadingOptions && (
        <ElementsBorder>
          <Field name="paymentOption">
            {({ field }) => (
              <RadioGroup
                name="paymentOption"
                wrapperProps={{ display: 'flex' }}
                {...field}
              >
                {availablePaymentOptions
                  ?.filter((option) => !!PAYMENT_OPTIONS_SETUP[option])
                  ?.map((option, index) => {
                    const { label, image } = PAYMENT_OPTIONS_SETUP[option]
                    return (
                      <RadioGroup.Item
                        key={option}
                        value={option}
                        borderBottom={
                          index !== availablePaymentOptions.length - 1 &&
                          `${BORDER_WIDTH.PX_1} solid ${COLOR_INTENT.GRAY_10}`
                        }
                        isFullWidth
                        padding={SPACE.PX_20}
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Text pl={SPACE.PX_10}>{label}</Text>
                          {image && (
                            <Image
                              src={image}
                              width={45}
                              height={28}
                              unoptimized
                              placeholder="empty"
                              alt={option}
                            />
                          )}
                        </Flex>
                      </RadioGroup.Item>
                    )
                  })}
              </RadioGroup>
            )}
          </Field>
          {paymentOption === PAYMENT_OPTIONS.CARD && (
            <Box px={SPACE.PX_20} pb={SPACE.PX_10}>
              {selectedCard && typeof selectedCard === 'object' ? (
                <Flex
                  flexDirection={{ _: 'column', DESKTOP: 'row' }}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <CardInputWrapper
                    alignItems="center"
                    width="100%"
                    mr={{ DESKTOP: SPACE.PX_10 }}
                    paddingX={SPACE.PX_15}
                    paddingY={SPACE.PX_10}
                  >
                    <Box mr={SPACE.PX_10}>
                      <Icon.CardOutlined
                        color={COLOR_INTENT.GRAY_70}
                        size={ICON_SIZE.PX_22}
                      />
                    </Box>
                    <Text
                      fontSize={{
                        MOBILE: FONT_SIZE.PX_12,
                        TABLET: FONT_SIZE.PX_14,
                      }}
                      color={COLOR_INTENT.TEXT_ON_DISABLED}
                    >
                      {formatSavedCreditCardText({
                        isMobile: media.MOBILE,
                        ...selectedCard,
                      })}
                    </Text>
                  </CardInputWrapper>
                  <Box
                    mt={{ _: SPACE.PX_10, DESKTOP: 0 }}
                    width={{ _: '100%', DESKTOP: 'auto' }}
                  >
                    <Button
                      isDisabled={isDisabled}
                      variant={BUTTON_VARIANT.OUTLINE}
                      onClick={() => {
                        setFieldValue(cardFieldName, null)
                      }}
                    >
                      Use different card
                    </Button>
                  </Box>
                </Flex>
              ) : (
                <>
                  <Field
                    name="card"
                    validate={(value) =>
                      value !== true && !value?.__typename && !isHidden
                        ? value || 'Card is a required field'
                        : undefined
                    }
                  >
                    {({ field: { onChange, value, ...restField }, meta }) => (
                      <Input
                        data-test-id={DATA_TEST_ID.CARD}
                        aria-label="Card input"
                        isError={meta.touched && meta.error !== undefined}
                        hint={meta.touched && meta.error ? meta.error : null}
                        hasHint
                        isCardInput
                        isDisabled={isDisabled}
                        onChange={(event) => {
                          setFieldValue(
                            cardFieldName,
                            event.complete || event.error?.message
                          )
                        }}
                        postalCode={postalCode}
                        {...restField}
                      />
                    )}
                  </Field>
                  <Grid gridGap={SPACE.PX_15}>
                    {user?.cards?.length > 0 && (
                      <Box>
                        <Text>Your saved credit cards</Text>
                      </Box>
                    )}
                    {user?.cards?.map((card) => (
                      <Flex
                        key={card.stripeId}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Flex alignItems="center">
                          <Box mr={SPACE.PX_10}>
                            <Icon.CardOutlined
                              color={COLOR_INTENT.GRAY_70}
                              size={ICON_SIZE.PX_22}
                            />
                          </Box>
                          <Text>
                            {formatSavedCreditCardText({
                              isMobile: media.MOBILE,
                              ...card,
                            })}
                          </Text>
                        </Flex>
                        <Box ml={SPACE.PX_10}>
                          <Button
                            isDisabled={isDisabled}
                            variant={BUTTON_VARIANT.OUTLINE}
                            onClick={() => {
                              setFieldValue(cardFieldName, card)
                            }}
                          >
                            Use this card
                          </Button>
                        </Box>
                      </Flex>
                    ))}
                  </Grid>
                </>
              )}
            </Box>
          )}
        </ElementsBorder>
      )}
    </Flex>
  )
}

PaymentElements.defaultProps = {
  cardFieldName: DEFAULT_CARD_FIELD_NAME,
  postalCode: '',
  isCustomCheckout: false,
}

PaymentElements.propTypes = {
  isDisabled: PropTypes.bool,
  card: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
    PropTypes.shape({
      stripeId: PropTypes.string,
      last4: PropTypes.string,
      expiryMonth: PropTypes.string,
      expiryYear: PropTypes.string,
    }),
  ]),
  paymentRequest: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  cardFieldName: PropTypes.string,
  paymentOption: PropTypes.oneOf(Object.values(PAYMENT_OPTIONS)).isRequired,
  availablePaymentOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoadingOptions: PropTypes.bool,
  isHidden: PropTypes.bool,
  postalCode: PropTypes.string,
  isCustomCheckout: PropTypes.bool,
  ...space.propTypes,
}
